/**
 * Mirrors the api's own badge constants and schemas (api/app/shared/constants/badge.ts,
 * api/app/shared/schemas/badge.schemas.ts) field for field, casing for casing.
 *
 * The split casing between the two enums below is deliberate on the api side, not a transcription
 * slip: `BADGE_CATEGORY` is a storage/partition key, so it ships lowercase; `BADGE_RARITY` is a
 * display label, so it ships Title Case. See the api's own `badge.ts` comment for the full reasoning.
 */
export enum BADGE_CATEGORY {
  FLAME = "flame",
  VALUE = "value",
  ACHIEVEMENT = "achievement",
  TENURE = "tenure",
  SEASONAL = "seasonal",
}

export enum BADGE_RARITY {
  COMMON = "Common",
  UNCOMMON = "Uncommon",
  RARE = "Rare",
  EPIC = "Epic",
  LEGENDARY = "Legendary",
  MYTHIC = "Mythic",
}

/** One rung of a flame or per-value ladder — mirrors `badgeLadderTierSchema`. */
export interface BadgeLadderTier {
  index: number;
  name: string;
  threshold: number;
}

/**
 * Lifetime flame progress. Lives on the badges response (`flame`), NOT on `SparksSummary` — see that
 * model's own comment; the old spec's `flame` field on the summary does not exist on the wire.
 */
export interface FlameProgress {
  lifetime_total: number;
  current_tier: BadgeLadderTier | null;
  next_tier: BadgeLadderTier | null;
  points_to_next: number | null;
  prestige_stars: number;
}

export interface PerValueProgress {
  core_value_id: number;
  value_lifetime_total: number;
  current_tier: BadgeLadderTier | null;
  next_tier: BadgeLadderTier | null;
  points_to_next: number | null;
}

/**
 * One row of `earned`. `awarded_at` is `z.date()` server-side but arrives as an ISO STRING over the
 * wire (JSON has no Date type) — typed `string`, not `Date`; calling `.toISOString()` on it would
 * throw at runtime despite compiling.
 */
export interface EarnedBadge {
  id: number;
  badge_definition_id: number;
  code: string;
  category: BADGE_CATEGORY;
  name: string;
  tier: number | null;
  rarity: BADGE_RARITY;
  icon_url: string | null;
  awarded_at: string;
  meta: Record<string, unknown> | null;
}

/**
 * `GET /recognitions/badges/:userId`'s response (`VIEW_BADGES`) — an OBJECT, not an array. Mirrors
 * `getUserBadgesResponseSchema` (api/app/shared/schemas/badge.schemas.ts) field for field.
 */
export interface UserBadgesResponse {
  earned: EarnedBadge[];
  flame: FlameProgress;
  per_value: PerValueProgress[];
}

/**
 * One row of the badge catalog (`GET /badge-definitions`, `VIEW_BADGES`) — mirrors
 * `badgeDefinitionRowSchema` (api/app/shared/schemas/badge.schemas.ts) field for field. `earned`
 * has no `description`, so this catalog row — not `EarnedBadge` — is the authoritative source of
 * display data for a badge card, earned or locked (923-core-context context/engineering/spark-rewards/phase-36-implementation-plan.md D3). Catalog
 * visibility (`is_active`) and award eligibility are different questions this endpoint does not
 * conflate — see that route's own doc comment.
 */
export interface BadgeDefinition {
  id: number;
  code: string;
  name: string;
  description: string | null;
  category: BADGE_CATEGORY;
  rarity: BADGE_RARITY;
  tier: number | null;
  sort_order: number | null;
  threshold: number | null;
  tenure_years: number | null;
  core_value_id: number | null;
  icon_url: string | null;
  is_active: boolean;
}

/**
 * `GET /badge-definitions`'s response. Mirrors `getBadgeDefinitionsResponseSchema` field for
 * field — `total` is part of the real wire shape but isn't consumed anywhere in this phase's UI
 * scope; the saga unwraps `results` and drops it before dispatching success.
 */
export interface GetBadgeDefinitionsResponse {
  results: BadgeDefinition[];
  total: number;
}
